// softphone-owner — does the signed-in session still belong to the person
// whose phone this tab registered?
//
// The session cookie is per BROWSER (Path=/, one cookie for every tab), but a
// SIP registration lives in one tab's sip.js UserAgent and answers to Asterisk,
// not to the cookie. So signing in as someone else in another tab replaced the
// session under the agent's desk while the desk stayed registered: the queue
// kept ringing it, the agent could answer, and everything that tab then sent
// (presence, call reports) went out as the NEW user (TC-A01.4).
//
// SipProvider records who owned the session when the phone was switched on and
// re-asks the server whenever that might have changed. This is the decision it
// makes with the answer, kept free of React so it can be tested.

/** What to do with the phone after asking the server who is signed in.
 *  - "claim": no owner recorded yet — this user is the owner from now on.
 *  - "keep":  same person; carry on.
 *  - "drop":  signed out, or someone else signed in; take the phone off. */
export type OwnerVerdict = "claim" | "keep" | "drop";

/**
 * @param ownerId  the user id recorded when the phone was switched on, or null
 *                 if none has been recorded yet.
 * @param currentId the user id the server reports for this browser's session
 *                 now, or null when nobody is signed in.
 */
export function judgeSessionOwner(ownerId: string | null, currentId: string | null): OwnerVerdict {
  if (currentId == null) return "drop";
  if (ownerId == null) return "claim";
  return ownerId === currentId ? "keep" : "drop";
}

/** Shown on the desk after the phone was taken off for this reason. */
export const SESSION_CHANGED_MESSAGE =
  "Your phone was signed out because a different account signed in (or you signed out) in this browser. Sign in again as yourself to take calls.";
